import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { 
  FaArrowRight, 
  FaBolt, 
  FaShieldAlt, 
  FaTruck,
  FaStar,
  FaCheckCircle
} from "react-icons/fa";
import { HiOutlineSparkles } from "react-icons/hi";

const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentWord, setCurrentWord] = useState(0);

  useEffect(() => {
    setIsVisible(true);
    
    // Rotating words animation
    const words = ["STRONGER", "LEANER", "BIGGER", "BETTER"];
    const interval = setInterval(() => {
      setCurrentWord((prev) => (prev + 1) % words.length);
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  const features = [
    { icon: FaBolt, text: "Fast Results", color: "from-amber-500 to-yellow-500" },
    { icon: FaShieldAlt, text: "100% Authentic", color: "from-green-500 to-emerald-500" },
    { icon: FaTruck, text: "Free Shipping", color: "from-blue-500 to-cyan-500" },
  ];

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-gray-50 to-white">
      
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-transparent z-10"></div>
        <img
          src="assets/images/hero_background.png"
          alt="Hero Background"
          className="w-full h-full object-cover object-center"
          loading="eager"
        />
        
        {/* Animated Overlay Pattern */}
        <div
          className="absolute inset-0 opacity-20 z-20"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23FFFFFF' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        ></div>      </div>

      {/* Floating Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-20">
        <div className="absolute top-20 left-1/4 w-2 h-2 bg-red-600 rounded-full animate-ping"></div>
        <div className="absolute bottom-20 right-1/4 w-3 h-3 bg-amber-600 rounded-full animate-ping animation-delay-1000"></div>
        <div className="absolute top-1/2 right-10 w-4 h-4 bg-blue-600 rounded-full animate-ping animation-delay-2000"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full z-30">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          
          {/* Left Content */}
          <div className={`text-white transform transition-all duration-1000 ${
            isVisible ? 'translate-x-0 opacity-100' : '-translate-x-20 opacity-0'
          }`}>
            {/* Trust Badges */}
            <div className="flex items-center gap-3 mb-6">
              <div className="flex -space-x-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="w-8 h-8 bg-gradient-to-br from-gray-700 to-gray-800 rounded-full border-2 border-white/20 flex items-center justify-center">
                    <span className="text-xs text-white font-bold">✓</span>
                  </div>
                ))}
              </div>
              <span className="text-sm text-gray-300">
                Trusted by <span className="font-bold text-white">10,000+</span> athletes
              </span>
            </div>

            {/* Main Heading */}
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold leading-tight mb-4">
              <span className="block bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                LIVE FIT
              </span>
              <span className="block mt-2">
                <span className="bg-gradient-to-r from-red-500 to-amber-500 bg-clip-text text-transparent">
                  WITH LESS FAT
                </span>
              </span>
            </h1>

            {/* Animated Subheading */}
            <div className="mt-4 mb-6">
              <p className="text-lg sm:text-xl text-gray-300">
                Find the perfect stack for{" "}
                <span className="relative inline-block">
                  <span className="absolute -inset-1 bg-gradient-to-r from-red-600/20 to-amber-600/20 blur-lg"></span>
                  <span className="relative text-white font-bold min-w-[120px] inline-block">
                    {["STRONGER", "LEANER", "BIGGER", "BETTER"][currentWord]}
                  </span>
                </span>
              </p>
            </div>

            {/* Features */}
            <div className="flex flex-wrap gap-4 mb-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20 hover:bg-white/20 transition-all duration-300"
                  >
                    <div className={`w-6 h-6 bg-gradient-to-br ${feature.color} rounded-full flex items-center justify-center`}>
                      <Icon className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-sm font-medium text-white">{feature.text}</span>
                  </div>
                );
              })}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <Link
                to="/shop"
                className="group relative inline-flex items-center gap-2 bg-gradient-to-r from-red-600 to-amber-600 hover:from-red-700 hover:to-amber-700 text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 shadow-xl hover:shadow-2xl hover:shadow-red-600/30 overflow-hidden"
              >
                <span className="relative z-10 flex items-center gap-2">
                  SHOP NOW
                  <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              </Link>

              <Link
                to="/about"
                className="group inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 border border-white/30"
              >
                LEARN MORE
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="mt-8 flex items-center gap-6">
              <div className="flex items-center gap-2">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <FaStar key={i} className="w-4 h-4 text-amber-500" />
                  ))}
                </div>
                <span className="text-sm text-gray-300">4.9/5 Rating</span>
              </div>
              <div className="flex items-center gap-2">
                <FaCheckCircle className="w-4 h-4 text-green-500" />
                <span className="text-sm text-gray-300">Verified Reviews</span>
              </div>
            </div>
          </div>

          {/* Right Image with Effects */}
          <div className={`hidden lg:block relative transform transition-all duration-1000 delay-500 ${
            isVisible ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
          }`}>
            {/* Glow Effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-red-600/30 to-amber-600/30 rounded-full blur-3xl animate-pulse"></div>
            
            {/* Main Image Container */}
            <div className="relative group">
              {/* Floating Badges */}
              <div className="absolute -top-6 -left-6 z-10 animate-bounce-slow">
                <div className="bg-gradient-to-br from-red-600 to-amber-600 text-white px-4 py-2 rounded-lg shadow-xl">
                  <p className="text-xs font-bold">BESTSELLER</p>
                  <p className="text-lg font-bold">#1</p>
                </div>
              </div>

              <div className="absolute -bottom-6 -right-6 z-10 animate-bounce-slow animation-delay-1000">
                <div className="bg-white/90 backdrop-blur-sm text-gray-900 px-4 py-2 rounded-lg shadow-xl">
                  <p className="text-xs font-bold">TRUSTED</p>
                  <p className="text-sm font-bold">10K+ REVIEWS</p>
                </div>
              </div>

              {/* Product Image */}
              <img
                src="https://darkdraganabolic.in/wp-content/uploads/2023/03/all-prod.png"
                alt="All Products"
                className="relative w-full h-auto object-contain transform group-hover:scale-105 transition-transform duration-700 drop-shadow-2xl"
                loading="eager"
              />

              {/* Product Tags */}
              <div className="absolute top-1/2 left-0 -translate-y-1/2 -translate-x-12 space-y-3">
                {["INJECTABLE", "ORAL ANABOLICS", "SUPPLEMENTS", " SPORTS NUTRITION"].map((tag, i) => (
                  <div
                    key={i}
                    className="bg-white/10 backdrop-blur-sm border border-white/20 text-white px-3 py-1.5 rounded-full text-xs font-bold hover:bg-white/20 transition-all duration-300 cursor-pointer"
                  >
                    {tag}
                  </div>
                ))}
              </div>
            </div>

            {/* Stats Cards */}
            <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 w-[80%] grid grid-cols-3 gap-2">
              {[
                { value: "50+", label: "Products" },
                { value: "15+", label: "Years" },
                { value: "10k+", label: "Customers" },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-2 text-center"
                >
                  <p className="text-white font-bold text-sm">{stat.value}</p>
                  <p className="text-gray-300 text-xs">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30">
        <div className="flex flex-col items-center gap-2">
          <span className="text-white/60 text-xs uppercase tracking-wider">Scroll</span>
          <div className="w-5 h-9 border-2 border-white/30 rounded-full flex justify-center">
            <div className="w-1 h-2 bg-white rounded-full mt-2 animate-bounce"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;